import { useCallback, useEffect, useState } from "react";
import PropTypes from "prop-types";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import DialogContentText from "@mui/material/DialogContentText";
import Icon from "@mui/material/Icon";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import { toast } from "react-hot-toast";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import PageErrorAlert, { PAGE_ERROR_TITLES } from "components/PageErrorAlert";
import DataTable from "examples/Tables/DataTable";
import CustomerDebitOrderEditDialog from "layouts/customer-view/components/CustomerDebitOrderEditDialog";
import DebitOrderSignaturePad from "layouts/customer-view/components/DebitOrderSignaturePad";
import { api } from "utils/api";
import { formatDate, formatDateTime } from "utils/dateFormatter";
import { isValidCustomerId } from "utils/customerUtils";

const ACCOUNT_TAB_BILLING = 0;
const ACCOUNT_TAB_DEBIT_ORDER = 1;

const fieldGridSx = {
  display: "grid",
  gridTemplateColumns: { xs: "1fr", sm: "160px 1fr" },
  gap: 1,
  "& > *": {
    minHeight: { xs: "auto", sm: "32px" },
    display: "flex",
    alignItems: { xs: "flex-start", sm: "center" },
  },
};

const tabShellSx = {
  mb: 2,
  p: 0.5,
  borderRadius: 2,
  bgcolor: "#f8f9fa",
  border: "1px solid #e0e0e0",
};

function formatMoney(value) {
  if (value === null || value === undefined || Number.isNaN(Number(value))) {
    return "—";
  }
  return `R ${Number(value).toFixed(2)}`;
}

function DebitOrderField({ label, value }) {
  const display =
    value === null || value === undefined || String(value).trim() === ""
      ? "—"
      : String(value);
  return (
    <>
      <MDTypography variant="body2" color="text">
        {label}:
      </MDTypography>
      <MDTypography variant="body2" fontWeight="medium">
        {display}
      </MDTypography>
    </>
  );
}

DebitOrderField.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

function renderTabLabel(iconName, label, isLoading = false) {
  return (
    <MDBox display="flex" alignItems="center" gap={1}>
      <Icon fontSize="small">{iconName}</Icon>
      <span>{label}</span>
      {isLoading && <CircularProgress size={12} thickness={6} color="inherit" />}
    </MDBox>
  );
}

function CustomerAccountBalancePanel({
  customerId,
  isManagerOrAdmin,
  balance,
  balanceLoading,
  balanceError,
  showBillingHistory,
  onToggleBillingHistory,
  onRefreshBillingHistory,
  billingCustomerId,
  onCreateQuote,
  onViewStatement,
  statementPreviewLoading,
  billingHistoryLoading,
  billingHistoryError,
  billingHistoryTable,
  billingHistoryCount,
}) {
  const [activeAccountTab, setActiveAccountTab] = useState(ACCOUNT_TAB_BILLING);
  const [debitOrder, setDebitOrder] = useState(null);
  const [debitOrderLoading, setDebitOrderLoading] = useState(false);
  const [debitOrderError, setDebitOrderError] = useState(null);
  const [debitOrderLoaded, setDebitOrderLoaded] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [debitOrderSaving, setDebitOrderSaving] = useState(false);

  const accountTabs = isManagerOrAdmin
    ? [
        { index: ACCOUNT_TAB_BILLING, icon: "account_balance", label: "Billing Info" },
        { index: ACCOUNT_TAB_DEBIT_ORDER, icon: "payments", label: "Debit Order" },
      ]
    : [];

  const fetchDebitOrder = useCallback(async () => {
    if (!isValidCustomerId(customerId)) {
      setDebitOrderError("Invalid customer id");
      setDebitOrder(null);
      return;
    }
    try {
      setDebitOrderLoading(true);
      setDebitOrderError(null);
      const response = await api.get(`/db/customers/${customerId}/debit-order`);
      setDebitOrder(response?.data ?? null);
      setDebitOrderLoaded(true);
    } catch (err) {
      const isNotFound =
        err?.status === 404 ||
        err?.response?.status === 404;
      if (isNotFound) {
        setDebitOrder(null);
        setDebitOrderError(null);
        setDebitOrderLoaded(true);
        return;
      }
      const message =
        err?.responseData?.error ||
        err?.response?.data?.error ||
        err?.technicalMessage ||
        err?.message ||
        "Failed to load debit order details";
      setDebitOrderError(message);
      setDebitOrder(null);
    } finally {
      setDebitOrderLoading(false);
    }
  }, [customerId]);

  useEffect(() => {
    setActiveAccountTab(ACCOUNT_TAB_BILLING);
    setDebitOrderLoaded(false);
    setDebitOrder(null);
    setDebitOrderError(null);
  }, [customerId]);

  useEffect(() => {
    setDebitOrderLoaded(false);
    setDebitOrder(null);
    setDebitOrderError(null);
    if (activeAccountTab === ACCOUNT_TAB_DEBIT_ORDER && isManagerOrAdmin) {
      void fetchDebitOrder();
    }
  }, [customerId, activeAccountTab, isManagerOrAdmin, fetchDebitOrder]);

  const handleDebitOrderSaved = (row) => {
    setDebitOrder(row);
    setDebitOrderLoaded(true);
    setDebitOrderError(null);
    toast.success("Debit order saved");
  };

  const handleDeleteDebitOrder = async () => {
    if (!debitOrder?.customer_debit_order_id) {
      return;
    }
    setDebitOrderSaving(true);
    try {
      await api.delete(
        `/db/customers/${customerId}/debit-order/${debitOrder.customer_debit_order_id}`
      );
      setDebitOrder(null);
      setDeleteDialogOpen(false);
      toast.success("Debit order deleted");
    } catch (err) {
      const message =
        err?.responseData?.error ||
        err?.technicalMessage ||
        err?.message ||
        "Failed to delete debit order";
      toast.error(message);
    } finally {
      setDebitOrderSaving(false);
    }
  };

  const renderDebitOrderActions = () => (
    <MDBox display="flex" gap={1} flexWrap="wrap" alignItems="center">
      <MDButton
        variant="outlined"
        color="info"
        size="small"
        onClick={fetchDebitOrder}
        disabled={debitOrderLoading}
      >
        Refresh
      </MDButton>
      {debitOrder ? (
        <>
          <MDButton
            variant="outlined"
            color="dark"
            size="small"
            startIcon={<EditIcon fontSize="small" />}
            onClick={() => setEditDialogOpen(true)}
          >
            Edit
          </MDButton>
          <MDButton
            variant="outlined"
            color="error"
            size="small"
            startIcon={<DeleteIcon fontSize="small" />}
            onClick={() => setDeleteDialogOpen(true)}
          >
            Delete
          </MDButton>
        </>
      ) : (
        debitOrderLoaded && (
          <MDButton
            variant="gradient"
            color="info"
            size="small"
            startIcon={<AddIcon fontSize="small" />}
            onClick={() => setEditDialogOpen(true)}
          >
            Add
          </MDButton>
        )
      )}
    </MDBox>
  );

  const renderTabButton = ({ index, icon, label, isLoading = false }) => {
    const isSelected = activeAccountTab === index;
    return (
      <MDButton
        key={index}
        role="tab"
        aria-selected={isSelected}
        onClick={() => setActiveAccountTab(index)}
        variant="text"
        color="dark"
        fullWidth
        sx={{
          minHeight: { xs: 48, md: 42 },
          py: 1,
          px: { xs: 1, md: 1.5 },
          fontWeight: 600,
          fontSize: { xs: "0.75rem", md: "0.875rem" },
          color: isSelected ? "#1A73E8" : "#344767",
          bgcolor: isSelected ? "#ffffff" : "transparent",
          borderRadius: 1.5,
          boxShadow: isSelected ? "0 1px 4px rgba(0,0,0,0.08)" : "none",
          justifyContent: "center",
          textTransform: "none",
          whiteSpace: "normal",
          lineHeight: 1.2,
          "&:hover": {
            bgcolor: isSelected ? "#ffffff" : "rgba(0, 0, 0, 0.04)",
          },
        }}
      >
        {renderTabLabel(icon, label, isLoading)}
      </MDButton>
    );
  };

  const renderBillingTab = () => (
    <MDBox display="flex" flexDirection="column" gap={3}>
      {balanceLoading ? (
        <MDBox display="flex" justifyContent="center">
          <CircularProgress />
        </MDBox>
      ) : balanceError ? (
        <PageErrorAlert title={PAGE_ERROR_TITLES.balance} message={balanceError} />
      ) : (
        <>
          <MDBox textAlign="center">
            <MDTypography
              variant="h4"
              color={
                balance?.balance > 0 ? "error" : balance?.balance < 0 ? "success" : "text"
              }
            >
              R{" "}
              {typeof balance?.balance === "number"
                ? balance.balance.toFixed(2)
                : "0.00"}
            </MDTypography>
            <MDTypography variant="body2" color="text">
              Current Balance
            </MDTypography>
            {balance?.lastUpdated && (
              <MDTypography variant="caption" color="text">
                Last updated: {formatDateTime(balance.lastUpdated)}
              </MDTypography>
            )}
          </MDBox>
          <MDBox display="flex" justifyContent="center" gap={1} flexWrap="wrap">
            {Number(billingCustomerId || 0) > 0 && onCreateQuote && (
              <MDButton variant="outlined" color="info" onClick={onCreateQuote}>
                Create Quotation
              </MDButton>
            )}
            {Number(billingCustomerId || 0) > 0 && (
              <MDButton
                variant="outlined"
                color="info"
                onClick={onViewStatement}
                disabled={statementPreviewLoading}
              >
                {statementPreviewLoading ? "Loading Statement..." : "View Statement"}
              </MDButton>
            )}
            {showBillingHistory && (
              <MDButton
                variant="outlined"
                color="info"
                onClick={onRefreshBillingHistory}
                disabled={billingHistoryLoading}
              >
                {billingHistoryLoading
                  ? "Refreshing Billing History..."
                  : "Refresh Billing History"}
              </MDButton>
            )}
            <MDButton
              variant="gradient"
              color="info"
              onClick={onToggleBillingHistory}
            >
              {showBillingHistory ? "Hide Billing History" : "View Billing History"}
            </MDButton>
          </MDBox>
        </>
      )}

      {showBillingHistory && (
        <MDBox mt={1}>
          {billingHistoryLoading ? (
            <MDBox display="flex" justifyContent="center">
              <CircularProgress />
            </MDBox>
          ) : billingHistoryError ? (
            <PageErrorAlert
              title={PAGE_ERROR_TITLES.billingHistory}
              message={billingHistoryError}
            />
          ) : billingHistoryCount > 0 ? (
            <DataTable
              table={billingHistoryTable}
              isSorted={false}
              entriesPerPage={false}
              showTotalEntries={true}
              noEndBorder
              autoResetPage={false}
            />
          ) : (
            <MDBox textAlign="center">
              <MDTypography variant="body2" color="text">
                No billing history available
              </MDTypography>
            </MDBox>
          )}
        </MDBox>
      )}
    </MDBox>
  );

  const renderDebitOrderTab = () => {
    if (debitOrderLoading && !debitOrderLoaded) {
      return (
        <MDBox display="flex" justifyContent="center" py={3}>
          <CircularProgress />
        </MDBox>
      );
    }
    if (debitOrderError) {
      return (
        <MDBox>
          <PageErrorAlert
            title="Debit order"
            message={debitOrderError}
          />
          <MDBox mt={2} display="flex" justifyContent="center">
            <MDButton variant="outlined" color="info" onClick={fetchDebitOrder}>
              Retry
            </MDButton>
          </MDBox>
        </MDBox>
      );
    }
    if (!debitOrder) {
      return (
        <MDBox display="flex" flexDirection="column" gap={2}>
          <MDBox
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            flexWrap="wrap"
            gap={1}
          >
            <MDTypography variant="button" color="text" fontWeight="medium">
              Mandate / banking (local)
            </MDTypography>
            {renderDebitOrderActions()}
          </MDBox>
          <MDBox textAlign="center" py={3}>
            <MDTypography variant="body2" color="text">
              No debit order record found for this customer.
            </MDTypography>
          </MDBox>
        </MDBox>
      );
    }

    return (
      <MDBox display="flex" flexDirection="column" gap={2}>
        <MDBox display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1}>
          <MDTypography variant="button" color="text" fontWeight="medium">
            Mandate / banking (local)
          </MDTypography>
          {renderDebitOrderActions()}
        </MDBox>
        <MDBox sx={fieldGridSx}>
          <DebitOrderField label="DOS reference" value={debitOrder.ref} />
          <DebitOrderField label="Customer code" value={debitOrder.customer_code} />
          <DebitOrderField label="State" value={debitOrder.state} />
          <DebitOrderField label="Account holder" value={debitOrder.name} />
          <DebitOrderField label="ID number" value={debitOrder.id} />
          <DebitOrderField label="Account number" value={debitOrder.account} />
          <DebitOrderField label="Branch code" value={debitOrder.branch_code} />
          <DebitOrderField label="Bank" value={debitOrder.bank_name} />
          <DebitOrderField label="Branch" value={debitOrder.branch} />
          <DebitOrderField label="Account type" value={debitOrder.type} />
          <DebitOrderField label="Debit day" value={debitOrder.day} />
          <DebitOrderField label="Recurring amount" value={formatMoney(debitOrder.recurring_amount)} />
          <DebitOrderField label="First amount" value={formatMoney(debitOrder.first_amount)} />
          <DebitOrderField label="Frequency" value={debitOrder.frequency} />
          <DebitOrderField label="Payments" value={debitOrder.payments} />
          <DebitOrderField
            label="First payment"
            value={debitOrder.first_payment_date ? formatDate(debitOrder.first_payment_date) : null}
          />
          <DebitOrderField
            label="Next payment"
            value={debitOrder.next_payment_date ? formatDate(debitOrder.next_payment_date) : null}
          />
          <DebitOrderField
            label="Last payment"
            value={debitOrder.last_payment_date ? formatDate(debitOrder.last_payment_date) : null}
          />
          <DebitOrderField
            label="Start date"
            value={debitOrder.start_date ? formatDate(debitOrder.start_date) : null}
          />
          <DebitOrderField
            label="Expiry date"
            value={debitOrder.expiry_date ? formatDate(debitOrder.expiry_date) : null}
          />
          <DebitOrderField label="Contact" value={debitOrder.contact} />
          <DebitOrderField label="Address" value={debitOrder.address} />
        </MDBox>
        <MDBox mt={1}>
          <DebitOrderSignaturePad value={debitOrder.signature} readOnly />
        </MDBox>
        {debitOrder.datetime && (
          <MDTypography variant="caption" color="text">
            Record updated: {formatDateTime(debitOrder.datetime)}
          </MDTypography>
        )}
      </MDBox>
    );
  };

  const renderAccountTabContent = () => {
    if (!isManagerOrAdmin || activeAccountTab === ACCOUNT_TAB_BILLING) {
      return renderBillingTab();
    }
    return renderDebitOrderTab();
  };

  return (
    <Card>
      <MDBox
        mx={2}
        mt={-3}
        py={3}
        px={2}
        variant="gradient"
        bgColor="info"
        borderRadius="lg"
        coloredShadow="info"
        display="flex"
        justifyContent="space-between"
        alignItems="center"
      >
        <MDTypography variant="h6" color="white">
          Billing & Payments
        </MDTypography>
      </MDBox>
      <CardContent>
        {isManagerOrAdmin && (
          <MDBox
            role="tablist"
            aria-label="Billing and payments sections"
            display="grid"
            gridTemplateColumns={{
              xs: "repeat(2, minmax(0, 1fr))",
            }}
            gap={0.5}
            sx={tabShellSx}
          >
            {accountTabs.map((tab) =>
              renderTabButton({
                ...tab,
                isLoading:
                  tab.index === ACCOUNT_TAB_DEBIT_ORDER ? debitOrderLoading : false,
              })
            )}
          </MDBox>
        )}
        {renderAccountTabContent()}
      </CardContent>

      <CustomerDebitOrderEditDialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        customerId={customerId}
        debitOrder={debitOrder}
        onSaved={handleDebitOrderSaved}
      />

      <Dialog open={deleteDialogOpen} onClose={() => !debitOrderSaving && setDeleteDialogOpen(false)}>
        <DialogTitle>Delete debit order?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This removes the local mandate record for this customer. It does not cancel the mandate
            at Direct Debit — update DOS separately if required.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <MDButton
            variant="outlined"
            color="secondary"
            onClick={() => setDeleteDialogOpen(false)}
            disabled={debitOrderSaving}
          >
            Cancel
          </MDButton>
          <MDButton
            variant="gradient"
            color="error"
            onClick={handleDeleteDebitOrder}
            disabled={debitOrderSaving}
          >
            {debitOrderSaving ? "Deleting…" : "Delete"}
          </MDButton>
        </DialogActions>
      </Dialog>
    </Card>
  );
}

CustomerAccountBalancePanel.propTypes = {
  customerId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  isManagerOrAdmin: PropTypes.bool.isRequired,
  balance: PropTypes.shape({
    balance: PropTypes.number,
    lastUpdated: PropTypes.string,
  }),
  balanceLoading: PropTypes.bool.isRequired,
  balanceError: PropTypes.string,
  showBillingHistory: PropTypes.bool.isRequired,
  onToggleBillingHistory: PropTypes.func.isRequired,
  onRefreshBillingHistory: PropTypes.func.isRequired,
  billingCustomerId: PropTypes.number,
  onCreateQuote: PropTypes.func,
  onViewStatement: PropTypes.func,
  statementPreviewLoading: PropTypes.bool,
  billingHistoryLoading: PropTypes.bool.isRequired,
  billingHistoryError: PropTypes.string,
  billingHistoryTable: PropTypes.object.isRequired,
  billingHistoryCount: PropTypes.number.isRequired,
};

CustomerAccountBalancePanel.defaultProps = {
  billingCustomerId: 0,
  onCreateQuote: undefined,
  onViewStatement: undefined,
  statementPreviewLoading: false,
};

export default CustomerAccountBalancePanel;
